import React, { useCallback, useEffect, useState } from 'react';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import {
  Tab,
  Tabs,
  TabList,
  TabPanel,
} from '@lifeomic/chroma-react/components/Tabs';
import SurveyResults from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/SurveyResults';
import SubjectViewerTableModule from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/Table';
import { ConfiguredMomentFormat } from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/util/momentFormats';
import {
  DashboardConfig,
  ModuleConfig,
} from '@lifeomic/phc-web-toolkit/src/components/LayoutConfiguration/types';
import { useHistory, useParams } from 'react-router-dom';
import urls from '../../../../util/urls';
import SubjectSurveys from '../../PatientDetail/ModuleTypes/SubjectSurveys';
// @ts-expect-error No declaration file for SubjectConsent
import SubjectConsent from '../../PatientDetail/ModuleTypes/SubjectConsent';
import MemberDetail from './index';

type ModuleComponentBase = { displayAsDuration: boolean };

const surveysModuleConfig: ModuleConfig<ModuleComponentBase> = {
  version: 1,
  module: {
    key: 'e3268880-e9e9-4b9d-ac51-a0e043c9ff42',
    moduleType: '9fddb278-f2db-44b5-b127-73eb4390a667',
    title: 'Surveys',
    layout: {
      w: 12,
      h: 18,
      x: 0,
      y: 0,
      minW: 2,
      minH: 0,
      moved: false,
      isResizable: true,
    },
    hasDynamicHeight: false,
    hasDynamicWidth: false,
    preCollapsedHeight: undefined,
    isHeaderlessModule: false,
  },
  component: { displayAsDuration: false },
};

const surveyResultsModuleConfig: ModuleConfig<ModuleComponentBase> = {
  version: 1,
  module: {
    key: '73090598-ee83-43cd-b559-ec80637f2402',
    moduleType: 'aa0e465b-369b-4627-bc7d-2315e5ae07b4',
    title: 'Survey Results',
    layout: {
      w: 12,
      h: 49,
      x: 0,
      y: 18,
      minW: 8,
      minH: 0,
      moved: false,
      isResizable: true,
    },
    hasDynamicHeight: false,
    hasDynamicWidth: false,
    preCollapsedHeight: undefined,
    isHeaderlessModule: false,
  },
  component: { displayAsDuration: false },
};

type SurveyCalculationsComponent = ModuleComponentBase & {
  ocrViewerBasePath: string;
  dataSource: string;
  columns: Array<{ key: string; label?: string; jmesPath?: string }>;
  momentFormat: ConfiguredMomentFormat;
  pageSize: number;
  useTagFilter: boolean;
  categories: [];
  useCategoryFilter: boolean;
  timeFilterProps: { Observation: 'date' };
  applyDateFilter: boolean;
  observationCodes: Array<{
    key: string;
    displayValue: string;
    display: string;
    code: string;
    system: string;
    below: [];
  }>;
  displayAllObservationCodes: boolean;
};

const surveyCalculationsModuleConfig: ModuleConfig<SurveyCalculationsComponent> =
  {
    version: 1,
    module: {
      key: '6429b113-1206-4c36-aadd-05edfda16afc',
      moduleType: '83387f5f-0e8c-456d-b528-7c1f62bcc5e4',
      title: 'Survey Result Calculations',
      layout: {
        w: 12,
        h: 19,
        x: 0,
        y: 67,
        minW: 2,
        minH: 0,
        moved: false,
        isResizable: true,
      },
      hasDynamicHeight: true,
      hasDynamicWidth: false,
      preCollapsedHeight: undefined,
    },
    component: {
      ocrViewerBasePath: '/project/ocr/viewer',
      dataSource: 'OBSERVATION_DATA_SOURCE_TYPE',
      columns: [
        {
          key: '630e576d-ef01-4f31-b9b5-2b3ec5bcffd7',
        },
        {
          label: 'Date',
          key: '911257ef-eb91-4c6f-b0b0-dbf1bc553f54',
        },
        {
          label: 'Value',
          key: 'b3d4b6a6-dc7b-436b-b034-4ae7007f41db',
        },
        {
          label: 'Interpretation',
          key: '88f766a2-e196-4474-98de-ed7b36c8c161',
          jmesPath: 'component[0].valueString',
        },
      ],
      displayAsDuration: false,
      momentFormat: 'M/D/YYYY' as ConfiguredMomentFormat,
      pageSize: 200,
      useTagFilter: false,
      categories: [],
      useCategoryFilter: false,
      timeFilterProps: {
        Observation: 'date',
      },
      applyDateFilter: false,
      observationCodes: [
        {
          key: 'PHQ9 Total Score',
          displayValue: 'PHQ9 Total Score',
          display: 'PHQ9 Total Score',
          code: 'PHQ9 Total Score',
          system: 'http://fountainlife.com/fhir',
          below: [],
        },
        {
          key: 'CAGE-AID Total Score',
          displayValue: 'CAGE-AID Total Score',
          display: 'CAGE-AID Total Score',
          code: 'CAGE-AID Total Score',
          system: 'http://fountainlife.com/fhir',
          below: [],
        },
        {
          key: 'PROMIS GMH-Tscore',
          displayValue: 'PROMIS Global Mental Health T-Score',
          display: 'PROMIS Global Mental Health T-Score',
          code: 'PROMIS GMH-Tscore',
          system: 'http://fountainlife.com/fhir',
          below: [],
        },
        {
          key: 'PROMIS GPH-Tscore',
          displayValue: 'PROMIS Global Physical Health T-Score',
          display: 'PROMIS Global Physical Health T-Score',
          code: 'PROMIS GPH-Tscore',
          system: 'http://fountainlife.com/fhir',
          below: [],
        },
        {
          key: '39156-5',
          displayValue: 'Body mass index (BMI) [Ratio]',
          display: 'Body mass index (BMI) [Ratio]',
          code: '39156-5',
          system: 'http://loinc.org',
          below: [],
        },
      ],
      displayAllObservationCodes: false,
    },
  };

const consentModuleConfig: ModuleConfig<ModuleComponentBase> = {
  version: 1,
  module: {
    key: '8a43577a-773d-4c17-8850-ae504d79ea23',
    moduleType: '57034953-07d6-435f-86d4-5351bae86581',
    title: 'Consent',
    layout: {
      w: 12,
      h: 26,
      x: 0,
      y: 86,
      minW: 2,
      minH: 0,
      moved: false,
      isResizable: true,
    },
    hasDynamicHeight: false,
    hasDynamicWidth: false,
    preCollapsedHeight: undefined,
    isHeaderlessModule: false,
  },
  component: { displayAsDuration: false },
};

const surveysDashboardConfig: DashboardConfig = {
  version: 3,
  modules: [
    surveysModuleConfig,
    surveyResultsModuleConfig,
    surveyCalculationsModuleConfig,
    consentModuleConfig,
  ],
};

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    minHeight: 0,
  },
  tabListWrapper: {
    width: '100%',
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
  },
  tabList: {
    flexShrink: 0,
    alignSelf: 'flex-start',
    display: 'inline-flex',
  },
  tabPanel: {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
    backgroundColor: theme.palette.common.white,
    position: 'relative',
  },
}));

type SurveysContentProps = {
  member: fhir.Patient | null;
  fallback: React.ReactNode;
};

type SurveysTab =
  | 'surveys'
  | 'responses'
  | 'results'
  | 'calculations'
  | 'consent';
type ResponsesTab = 'all' | 'complete' | 'incomplete';

const isValidTab = (tab?: string): tab is SurveysTab => {
  return (
    tab === undefined ||
    tab === 'surveys' ||
    tab === 'responses' ||
    tab === 'results' ||
    tab === 'calculations' ||
    tab === 'consent'
  );
};

const isValidResponsesTab = (tab?: string): tab is ResponsesTab => {
  return (
    tab === undefined ||
    tab === 'all' ||
    tab === 'complete' ||
    tab === 'incomplete'
  );
};

const responsesTabToStatusFilter = (
  tab: ResponsesTab,
): 'all' | 'completed' | 'in-progress' => {
  if (tab === 'complete') {
    return 'completed';
  }
  if (tab === 'incomplete') {
    return 'in-progress';
  }
  return 'all';
};

const statusFilterToResponsesTab = (
  status: 'all' | 'completed' | 'in-progress',
): ResponsesTab => {
  if (status === 'completed') {
    return 'complete';
  }
  if (status === 'in-progress') {
    return 'incomplete';
  }
  return 'all';
};

export const SurveysContent: React.FC<SurveysContentProps> = ({
  member,
  fallback,
}) => {
  const classes = useStyles({});
  const history = useHistory();
  const { surveysTab, responsesTab, memberId } = useParams<{
    surveysTab?: string;
    responsesTab?: string;
    memberId: string;
  }>();
  const selectedTab: SurveysTab = isValidTab(surveysTab)
    ? surveysTab || 'surveys'
    : 'surveys';
  const selectedResponsesTab: ResponsesTab = isValidResponsesTab(responsesTab)
    ? responsesTab || 'all'
    : 'all';
  const [lastResponsesTab, setLastResponsesTab] =
    useState<ResponsesTab>(selectedResponsesTab);
  const getSurveysPath = useCallback(
    (tab: SurveysTab, responseTab: ResponsesTab = 'all') => {
      const basePath = `${urls.app.members(memberId)}/surveys`;
      if (tab === 'surveys') {
        return basePath;
      }
      if (tab === 'responses') {
        return `${basePath}/${tab}/${responseTab}`;
      }
      return `${basePath}/${tab}`;
    },
    [memberId],
  );

  useEffect(() => {
    if (!isValidTab(surveysTab)) {
      history.replace(getSurveysPath('surveys'));
      return;
    }
    if (selectedTab === 'responses' && !isValidResponsesTab(responsesTab)) {
      history.replace(getSurveysPath('responses', 'all'));
      return;
    }
    if (selectedTab !== 'responses' && responsesTab !== undefined) {
      history.replace(getSurveysPath(selectedTab));
    }
  }, [getSurveysPath, history, responsesTab, selectedTab, surveysTab]);

  useEffect(() => {
    if (selectedTab === 'responses' && isValidResponsesTab(responsesTab)) {
      setLastResponsesTab(selectedResponsesTab);
    }
  }, [responsesTab, selectedResponsesTab, selectedTab]);

  const injectedPropsBase = {
    isModuleVisible: true,
    dateTickFilter: { min: 0, max: 0 },
    dateTickBounds: { min: 0, max: 0 },
    dateFilter: { min: new Date(0), max: new Date() },
    isInEditMode: false,
    deviceId: 'member-detail',
    onModuleConfigChange: () => undefined,
    onDynamicDataResolved: () => ({}),
    selectedDashboardConfig: surveysDashboardConfig,
  } as const;

  if (!member) {
    return <>{fallback}</>;
  }

  const patient = { ...member, id: member.id ?? '' } as fhir.Patient & {
    id: string;
  };

  return (
    <div className={classes.root}>
      <div className={classes.tabsContainer}>
        <Tabs selectedTabId={selectedTab} variant="pill">
          <div className={classes.tabsRoot}>
            <div className={classes.tabListWrapper}>
              <TabList aria-label="Survey sections" className={classes.tabList}>
                <Tab
                  stopId="surveys"
                  onClick={() => history.push(getSurveysPath('surveys'))}
                >
                  {surveysModuleConfig.module.title}
                </Tab>
                <Tab
                  stopId="responses"
                  onClick={() =>
                    history.push(getSurveysPath('responses', lastResponsesTab))
                  }
                >
                  Responses
                </Tab>
                <Tab
                  stopId="results"
                  onClick={() => history.push(getSurveysPath('results'))}
                >
                  {surveyResultsModuleConfig.module.title}
                </Tab>
                <Tab
                  stopId="calculations"
                  onClick={() => history.push(getSurveysPath('calculations'))}
                >
                  {surveyCalculationsModuleConfig.module.title}
                </Tab>
                <Tab
                  stopId="consent"
                  onClick={() => history.push(getSurveysPath('consent'))}
                >
                  {consentModuleConfig.module.title}
                </Tab>
              </TabList>
            </div>
            <TabPanel stopId="surveys" className={classes.tabPanel}>
              <SubjectSurveys
                {...injectedPropsBase}
                moduleKey={surveysModuleConfig.module.key}
                moduleConfig={surveysModuleConfig}
                patient={patient}
                dateTickFilter={injectedPropsBase.dateTickFilter}
                applyDateFilter={false}
                view="surveys"
              />
            </TabPanel>
            <TabPanel stopId="responses" className={classes.tabPanel}>
              <SubjectSurveys
                {...injectedPropsBase}
                moduleKey={surveysModuleConfig.module.key}
                moduleConfig={surveysModuleConfig}
                patient={patient}
                dateTickFilter={injectedPropsBase.dateTickFilter}
                applyDateFilter={false}
                view="responses"
                responseStatusFilter={responsesTabToStatusFilter(
                  selectedResponsesTab,
                )}
                onResponseStatusFilterChange={status => {
                  const nextResponsesTab = statusFilterToResponsesTab(status);
                  setLastResponsesTab(nextResponsesTab);
                  history.push(getSurveysPath('responses', nextResponsesTab));
                }}
              />
            </TabPanel>
            <TabPanel stopId="results" className={classes.tabPanel}>
              <SurveyResults
                {...injectedPropsBase}
                moduleKey={surveyResultsModuleConfig.module.key}
                moduleConfig={surveyResultsModuleConfig}
                patient={patient}
              />
            </TabPanel>
            <TabPanel stopId="calculations" className={classes.tabPanel}>
              <SubjectViewerTableModule
                {...injectedPropsBase}
                moduleKey={surveyCalculationsModuleConfig.module.key}
                moduleConfig={surveyCalculationsModuleConfig}
                patient={patient}
                dataSource="OBSERVATION_DATA_SOURCE_TYPE"
                columns={surveyCalculationsModuleConfig.component.columns}
                momentFormat={
                  surveyCalculationsModuleConfig.component.momentFormat
                }
                pageSize={surveyCalculationsModuleConfig.component.pageSize}
                useTagFilter={
                  surveyCalculationsModuleConfig.component.useTagFilter
                }
                categories={surveyCalculationsModuleConfig.component.categories}
                useCategoryFilter={
                  surveyCalculationsModuleConfig.component.useCategoryFilter
                }
                timeFilterProps={
                  surveyCalculationsModuleConfig.component.timeFilterProps
                }
                applyDateFilter={
                  surveyCalculationsModuleConfig.component.applyDateFilter
                }
                observationCodes={
                  surveyCalculationsModuleConfig.component.observationCodes
                }
                displayAllObservationCodes={
                  surveyCalculationsModuleConfig.component
                    .displayAllObservationCodes
                }
              />
            </TabPanel>
            <TabPanel stopId="consent" className={classes.tabPanel}>
              <SubjectConsent patient={patient} />
            </TabPanel>
          </div>
        </Tabs>
      </div>
    </div>
  );
};

const MemberSurveys: React.FC = () => {
  return <MemberDetail section="surveys" />;
};

MemberSurveys.displayName = 'MemberSurveys';
export default MemberSurveys;
